import type { ChangeEvent } from 'react';
import React, { useEffect, useRef } from 'react';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { DropDown, IconInfoCircular } from '@noths/polaris-client-ribbons-design-system';

import { DeliveryOptions } from 'src/components/molecules/DeliveryOptions/DeliveryOptions';
import { PhotoPersonalisationMessage } from 'src/components/molecules/PhotoPersonalisationMessage/PhotoPersonalisationMessage';
import {
  COUNTRY_SELECT_LABEL,
  DELIVERY_NOT_AVAILABLE_TO_COUNTRY_FIELD_ERROR,
  DELIVERY_NOT_AVAILABLE_TO_COUNTRY_MESSAGE,
  TIMEZONE_MESSAGE,
} from './copy';
import type { DeliveryBlockProps } from './types';
import * as styles from './DeliveryBlock.styles';

export const DeliveryBlock = ({
  arrivesBy,
  deliveryOptions,
  deliveryOptionsForCurrentZone,
  groupedCountries,
  hasAnyDeliveryZones,
  onCountryChange,
  onVisible,
  partnerName,
  photoUploadRequired,
  selectedCountry,
}: DeliveryBlockProps) => {
  const deliverySectionRef = useImpressionRef<HTMLDivElement>(() => onVisible());

  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const [countryCode, deliveryZone] = e.target.value.split('|') as [string, DeliveryZoneCode];

    onCountryChange({
      selectedCountry: countryCode,
      selectedZone: deliveryZone,
    });
  };

  const isDeliveryAvailableForCurrentZone = Boolean(deliveryOptionsForCurrentZone.length);
  const showTimezoneMessage = useRef(false);

  useEffect(() => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    showTimezoneMessage.current = userTimeZone !== 'Europe/London';
  }, []);

  if (!hasAnyDeliveryZones) {
    return null;
  }

  return (
    <section css={styles.deliveryBlock} ref={deliverySectionRef}>
      <header>
        <h2>Delivery info</h2>
        <DropDown
          css={styles.countrySelect}
          error={
            !isDeliveryAvailableForCurrentZone
              ? DELIVERY_NOT_AVAILABLE_TO_COUNTRY_FIELD_ERROR
              : null
          }
          id="delivery-block-countries"
          label={COUNTRY_SELECT_LABEL}
          labelHidden
          onChange={handleCountryChange}
          options={groupedCountries}
          value={selectedCountry}
        />
        {showTimezoneMessage.current && (
          <div css={styles.timezoneMessageWrapper}>
            <IconInfoCircular height={16} width={16} />
            <p css={styles.timezoneMessage}>{TIMEZONE_MESSAGE}</p>
          </div>
        )}
      </header>
      {isDeliveryAvailableForCurrentZone ? (
        <DeliveryOptions
          arrivesBy={arrivesBy}
          deliveryOptions={deliveryOptions}
          partnerName={partnerName}
        />
      ) : (
        <p css={styles.deliveryNotAvailableMessage}>{DELIVERY_NOT_AVAILABLE_TO_COUNTRY_MESSAGE}</p>
      )}
      {photoUploadRequired && (
        <>
          <hr />
          <PhotoPersonalisationMessage />
        </>
      )}
    </section>
  );
};
